import type { ComputedRef, Ref } from "vue";
import type { ISwiperConfig } from "~/types/swipe-bet/config";
import { STATUS_CONSTANTS } from "./statusConstants";
import { type ISwiperState, initState } from "./swiperState";
import { isMobile } from "~/utils/constants";
import type { TSwiperDecision } from "./swiperCore";

const useSwiperTouchEvents = (
	swiperConfig: Ref<ISwiperConfig>,
	swiperState: Ref<ISwiperState>,
	pointerOpacity: ComputedRef<number>,
	superOpacity: ComputedRef<number>,
	downOpacity: ComputedRef<number>,
	shiftCard: (result: TSwiperDecision) => void
) => {
	const start = (e: TouchEvent | MouseEvent) => {
		if (
			swiperState.value.touchId !== null ||
			swiperState.value.status === STATUS_CONSTANTS.LEAVING ||
			swiperState.value.status === STATUS_CONSTANTS.REVERT ||
			swiperState.value.status === STATUS_CONSTANTS.REWINDING
		) {
			return;
		}

		let pageX: number;
		let pageY: number;
		if (e.type === "touchstart") {
			pageX = (e as TouchEvent).changedTouches[0].pageX;
			pageY = (e as TouchEvent).changedTouches[0].pageY;
		} else {
			pageX = (e as MouseEvent).clientX;
			pageY = (e as MouseEvent).clientY;
		}

		const top = swiperConfig.value.size.top;
		const height = swiperConfig.value.size.height;
		const centerY = top + height / 2;
		const startPoint = pageY > centerY ? -1 : 1;

		swiperState.value = {
			status: STATUS_CONSTANTS.MOVING, // Back to immediate moving for proper drag detection
			touchId: e.type === "touchstart" ? (e as TouchEvent).changedTouches[0].identifier : "mouse",
			start: {
				x: pageX,
				y: pageY,
			},
			move: Object.create(null),
			startPoint,
			result: null,
		};
	};
	const move = (e: TouchEvent | MouseEvent) => {
		e.preventDefault();
		if (
			swiperState.value.touchId === null ||
			swiperState.value.status === STATUS_CONSTANTS.LEAVING ||
			swiperState.value.status === STATUS_CONSTANTS.REVERT ||
			swiperState.value.status === STATUS_CONSTANTS.REWINDING ||
			(e.type === "touchmove" && swiperState.value.touchId !== (e as TouchEvent).changedTouches[0].identifier)
		) {
			return;
		}

		let pageX: number;
		let pageY: number;
		if (e.type === "touchmove") {
			pageX = (e as TouchEvent).changedTouches[0].pageX;
			pageY = (e as TouchEvent).changedTouches[0].pageY;
		} else {
			pageX = (e as MouseEvent).clientX;
			pageY = (e as MouseEvent).clientY;
		}

		swiperState.value.move = {
			x: pageX,
			y: pageY,
		};

		// Auto-swipe when card reaches screen edges (desktop only)
		// const isMobile = "ontouchstart" in window || navigator.maxTouchPoints > 0;

		if (!isMobile && swiperState.value.status === STATUS_CONSTANTS.MOVING) {
			const screenWidth = window.innerWidth;
			const screenHeight = window.innerHeight;
			const edgeThreshold = screenWidth * 0.05; // 10% from edge
			const verticalEdgeThreshold = screenHeight * 0.05; // 10% from top/bottom
			const cardCenterX = pageX;
			const cardCenterY = pageY;
			const screenCenterX = screenWidth / 2;

			// Check if card has reached any edge
			const hasReachedEdge =
				cardCenterX <= edgeThreshold || // Left edge
				cardCenterX >= screenWidth - edgeThreshold || // Right edge
				cardCenterY <= verticalEdgeThreshold || // Top edge
				cardCenterY >= screenHeight - verticalEdgeThreshold; // Bottom edge

			if (hasReachedEdge) {
				// Determine action based on which side of screen center the card is on
				const action = cardCenterX >= screenCenterX ? "like" : "nope";
				shiftCard(action);
				return;
			}
		}
	};
	const end = (e: TouchEvent | MouseEvent) => {
		e.preventDefault();
		if (e.type === "touchstart" && swiperState.value.touchId !== (e as TouchEvent).changedTouches[0].identifier) {
			return;
		}
		if (
			swiperState.value.status === STATUS_CONSTANTS.LEAVING ||
			swiperState.value.status === STATUS_CONSTANTS.REVERT ||
			swiperState.value.status === STATUS_CONSTANTS.REWINDING
		) {
			return;
		}

		if (Math.abs(pointerOpacity.value) >= 1 || superOpacity.value >= 1 || downOpacity.value >= 1) {
			const result = superOpacity.value >= 1 ? "super" : downOpacity.value >= 1 ? "down" : pointerOpacity.value > 0 ? "like" : "nope";
			shiftCard(result);
		} else if (swiperState.value.status === STATUS_CONSTANTS.MOVING) {
			swiperState.value = initState(true);
		}
	};
	return {
		start,
		move,
		end,
	};
};

export default useSwiperTouchEvents;
